<template>
  <div>
    <b-card
      no-body
    >
      <div>
        <div
          class="m-2"
        >
          <!-- input -->
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <b-button
                v-can="'create-merchant-users'"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                class="custom-button-color"
                @click="onClickOpenAddMerchantUserModal"
              >
                <feather-icon icon="PlusIcon" />
                Add New
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- table -->
        <b-overlay
          id="overlay-background"
          variant="light"
          opacity="0.30"
          blur="10px"
          rounded="sm"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="rows"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            class="hide-vertical-scroll"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #cell(name)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  size="32"
                  :text="avatarText(data.item.name)"
                  variant="light-primary"
                />
                <div>
                  <div class="font-weight-bolder ml-1">
                    {{ data.item.name }}
                  </div>
                </div>
              </div>
            </template>
            <template #cell(created_at)="data">
              <div class="d-flex align-items-center">
                <div>
                  <div class="font-weight-bolder ml-1">
                    {{ timestampToDateTime(data.item.created_at) }}
                  </div>
                </div>
              </div>
            </template>

            <template #cell(is_active)="data">
              {{ data.value }}
              <b-badge :variant="status[1][1]">
                {{ status[0][1] }}
              </b-badge>
            </template>

            <template #cell(action)="data">
              {{ data.value }}
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="12"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  v-can="'show-merchant-users'"
                  @click="onClickOpenManageUserModal(data.item.id)"
                >
                  <feather-icon icon="ToolIcon" />
                  Manage User
                </b-dropdown-item>
                <b-dropdown-item
                  v-can="'reset-password-merchant-users'"
                  @click="onClickResetPassword(data.item)"
                >
                  <feather-icon icon="LockIcon" />
                  Reset User Password
                </b-dropdown-item>
                <!--                <b-dropdown-item-->
                <!--                  @click="onClickOpenDeleteUserConfirm(data.item.id)"-->
                <!--                >-->
                <!--                  <feather-icon icon="ToolIcon" />-->
                <!--                  Delete User-->
                <!--                </b-dropdown-item>-->
              </b-dropdown>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                md="3"
                class="d-flex align-items-center justify-content-sm-start"
              >
                <label>Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block ml-1 width-100 mr-1"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                md-9
                class="d-flex align-items-center justify-content-sm-end"
              >
                <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :total-rows="0"
                  :per-page="perPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </div>
    </b-card>

    <create-merchant-user-modal ref="add_new_merchant_user_modal" />
    <update-merchant-user-modal ref="update_merchant_user_modal" />
    <reset-password-merchant-user-modal ref="resetPasswordMerchantUserModal" />
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BOverlay,
  VBModal,
  BTable,
  BDropdown,
  BDropdownItem,
  BBadge,
  BAvatar,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import vSelect from 'vue-select'
import { avatarText } from '@core/utils/filter'
import { timestampToDateTime } from '@core/utils/timeUtil'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import store from '@/store'
import CreateMerchantUserModal from '@/components/merchant/CreateMerchantUserModal.vue'
import UpdateMerchantUserModal from '@/components/merchant/UpdateMerchantUserModal.vue'
import ResetPasswordMerchantUserModal from '@/components/merchant/ResetPasswordMerchantUserModal.vue'

const MerchantRepository = RepositoryFactory.get('merchant')

function initialState() {
  return {
    avatarText,
    timestampToDateTime,
    filters: {},
    total: 0,
    meta: {},
    dataLoading: false,
    sort: '',
    perPage: 20,
    pageOptions: [10, 20, 50],
    totalRows: 1,
    page: 1,
    sortBy: '',
    sortDesc: false,
    sortDirection: 'asc',
    fields: [
      {
        key: 'id', label: 'Ref No', sortable: true, thClass: 'd-none', tdClass: 'd-none',
      },
      { key: 'first_name', label: 'First Name', sortable: true },
      { key: 'last_name', label: 'Last Name', sortable: false },
      { key: 'email', label: 'Email', sortable: false },
      { key: 'phone', label: 'Contact No', sortable: false },
      { key: 'created_at', label: 'Created At', sortable: true },
      { key: 'action', label: 'Action', sortable: false },
    ],
    rows: [],
    business_id: null,
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BPagination,
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BOverlay,
    vSelect,
    BDropdown,
    BDropdownItem,
    BBadge,
    BAvatar,
    CreateMerchantUserModal,
    UpdateMerchantUserModal,
    ResetPasswordMerchantUserModal,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    page() {
      this.fetchMerchantUserList()
    },
    perPage() {
      this.fetchMerchantUserList()
    },
  },
  mounted() {
    // Set the initial number of items
    // eslint-disable-next-line prefer-destructuring
    this.business_id = window.location.href.split('/').reverse()[0]
    this.fetchMerchantUserList()
  },
  methods: {
    onClickRefresh() {
      this.fetchMerchantUserList()
    },
    onClickOpenAddMerchantUserModal() {
      this.$refs.add_new_merchant_user_modal.openModal(this.$route.params.id, this.$route.params.businessId)
    },
    onClickOpenManageUserModal(id) {
      this.$refs.update_merchant_user_modal.openModal(this.$route.params.id, id, 'user')
    },
    onClickOpenDeleteUserConfirm() {
      // Set Merchant User Delete Confirm Modal
    },
    async fetchMerchantUserList() {
      this.dataLoading = true
      this.rows = []
      try {
        const { data } = (await MerchantRepository.getMerchantUserList(this.$route.params.id, this.business_id))
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    onClickResetPassword(data) {
      this.$refs.resetPasswordMerchantUserModal.openModal(data)
    },

    handleChangePage(page) {
      this.page = page
      this.fetchMerchantUserList()
    },
    changePage(value) {
      this.perPage = value
    },
    // REQUIRED
  },
}
</script>
<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
</style>
